import React, { useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { ApplicationState } from 'store';
import {
  createUserChatMessage,
  createUserChat,
  fetchUserChatMessages,
} from 'store/modules/chatbot_assistant_store/actions';
import ChatbotMessage from 'components/atoms/chatbot_message';
import {
  ChatbotAssistantState,
  CurrentUserChatMessageTypes,
} from 'store/modules/chatbot_assistant_store/types';
import ChatbotInput from 'components/atoms/chatbot_input';
import SquareLoader from 'components/atoms/square_loader';
import { MenusBehaviourTypes } from 'store/modules/menus_behaviour_store/types';
import ChatbotTypingRow from 'components/molecules/chatbot_typing_row';
import { Button } from './styles';

interface ChatbotAssistantChatProps {
  isLoading: boolean;
}

const ChatbotAssistantChat = (props: ChatbotAssistantChatProps) => {
  const { isLoading } = props;
  const dispatch = useDispatch();
  const componentRef = useRef() as React.MutableRefObject<HTMLInputElement>;
  const chatRef = useRef() as React.MutableRefObject<HTMLInputElement>;

  const firstMessage: CurrentUserChatMessageTypes = {
    id: null,
    role: 'assistant',
    content:
      'Bem-vindo ao Consultor Jurídico IA, seu especialista em dúvidas jurídicas sobre licitações e contratos administrativos, com foco na prática do dia a dia. Se precisar de suporte jurídico especializado, conte também com o Jurídico do ConLicitação.',
    created_at: '',
    updated_at: '',
    rating: null,
  };

  const { chatbot_assistant, request } = useSelector<ApplicationState, ChatbotAssistantState>(
    (state) => state.session.chatbot_assistant,
  );

  const openFeedBackModal = () => {
    dispatch({ type: MenusBehaviourTypes.OPEN_FEEDBACK_MODAL, pathname: 'Especialista virtual' });
    dispatch({ type: MenusBehaviourTypes.CLOSE_CHATBOT_ASSISTANT });
  };

  const sendMessage = (text?: string) => {
    const content = text ?? componentRef.current?.innerText?.trim();

    if (!content) return;

    dispatch(
      createUserChatMessage({
        content,
        assistant_user_chat_id: chatbot_assistant.current_chat_id || null,
      }),
    );

    if (componentRef.current) {
      componentRef.current.innerText = '';
    }
  };

  useEffect(() => {
    if (chatbot_assistant.current_chat_id)
      dispatch(
        fetchUserChatMessages({
          assistant_user_chat_id: chatbot_assistant.current_chat_id,
          is_current_chat: true,
        }),
      );
  }, []);

  useEffect(() => {
    if (!chatbot_assistant.current_chat_id) dispatch(createUserChat());
  }, [chatbot_assistant.current_chat_id]);

  useEffect(() => {
    if (chatbot_assistant.current_chat_messages?.length === 0) return;
    if (!chatRef?.current) return;

    (chatRef?.current?.lastChild as HTMLElement).scrollIntoView({ behavior: 'smooth' });
  }, [chatbot_assistant.current_chat_messages, isLoading]);

  if (request.create_user_chat.loading)
    return (
      <div className="d-flex text-white align-items-center justify-content-center h-100">
        <div className="position-relative">
          Carregando <SquareLoader customClass="chatbot-loading" />
        </div>
      </div>
    );

  return (
    <>
      <div className="chatbot-body">
        <div ref={chatRef} className="chatbot-messages">
          <ChatbotMessage message={firstMessage} source="chat" firstMessage />
          {chatbot_assistant.current_chat_messages?.map((message, index) => (
            <ChatbotMessage
              message={message}
              source="chat"
              key={
                message.id != null
                  ? String(message.id)
                  : `assistant-msg-${message.created_at}-${index}`
              }
            />
          ))}
          {isLoading && <ChatbotTypingRow variant="consultor_chat" />}
          {request.create_user_chat_message.error && (
            <div className="chatbot-warning text-white border-white p-2 mx-auto rounded text-center my-2 ml-2 border">
              Ocorreu um erro. Faça a pergunta novamente.
            </div>
          )}
        </div>
      </div>
      <Button
        type="button"
        className="btn btn-link text-center text-white"
        onClick={() => openFeedBackModal()}
      >
        O que esta achando da ferramenta? Dê um feedback!
      </Button>
      <ChatbotInput
        componentRef={componentRef}
        isLoading={isLoading}
        sendMessage={sendMessage}
        source="consultor-juridico"
        variant="dark"
      />
    </>
  );
};

export default ChatbotAssistantChat;
